import { GetServerSideProps } from 'next'
import { useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'
import { CourseChapter, GrowthCategory, GrowthContent } from '@mc/api'

import Link from 'components/Link'

import { getPageData } from 'libs/server/page-data'
import { chapterPath, sitemapPath } from 'libs/paths'
import {
  getCourse,
  getGrowthClassBySlug,
  getV2Categories,
} from 'libs/server/api'
import { pageErrorHandler } from 'libs/server/page-error-handler'

import Layout from '../components/Layout'

const CoursePage = ({
  category,
  course,
  sortedChapters,
}: {
  category: GrowthCategory
  course: GrowthContent
  sortedChapters: CourseChapter[]
}) => {
  const { t } = useTranslation('@mc/next')

  return (
    <Layout>
      <div className='row'>
        <div className='col-12'>
          <h2 className='mc-text-h4 mc-text--uppercase mc-mb-8'>
            <Link href={sitemapPath()}>{t('sitemap.homeBreadcrumb')}</Link>
            {' > '}
            <Link href={paths.sitemapCategories(category?.slug as string)}>
              {category?.name}
            </Link>
            {' > '}
            {course.title}
          </h2>
          <ul className='row'>
            {sortedChapters?.map(({ slug: lessonSlug, title }) => (
              <li key={lessonSlug} className='col-12 col-md-6'>
                <Link passHref href={chapterPath(course.slug, lessonSlug)}>
                  {title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const {
    query: { slug },
  } = ctx
  if (typeof slug !== 'string') {
    return { notFound: true }
  }

  let sortedChapters: CourseChapter[] = []

  try {
    const data = await getCourse(ctx, { slug })
    sortedChapters = data.sortedChapters || []
  } catch (error) {
    return pageErrorHandler(error)
  }

  return getPageData({
    criticalPromises: {
      course: getGrowthClassBySlug(ctx, slug),
    },
    nonCriticalPromises: {
      categories: getV2Categories(ctx),
    },
    propsMapper: (props) => ({
      ...props,
      category: props.categories?.find(
        (cat) => cat.id === props.course?.primaryCategory?.id,
      ),
      sortedChapters,
    }),
  })
}

export default CoursePage
